package org.vreo.devicehub.features.core.presentation.components.largeDropdownMenu

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.ArrowDropUp
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog

/**
 * Large dropdown menu. Displays list of items and allows to select one.
 *
 * @param items list of items to display
 * @param selectedIndex index of the selected item
 * @param onItemSelected action handler for item selection, returns selected item index
 * @param modifier modifier for the dropdown menu
 * @param label label of the dropdown menu
 */
@Composable
fun LargeDropdownMenu(
    items: List<String>,
    selectedIndex: Int,
    onItemSelected: (Int) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
) {
    var expanded by remember { mutableStateOf(false) }

    Box(
        modifier = modifier
            .height(IntrinsicSize.Min)
    ) {
        OutlinedTextField(
            label = {
                if (label != null) {
                    Text(label)
                }
            },
            value = items.getOrNull(selectedIndex) ?: "",
            onValueChange = {},
            readOnly = true,
            trailingIcon = {
                Icon(
                    imageVector = if (expanded) {
                        Icons.Default.ArrowDropUp
                    } else {
                        Icons.Default.ArrowDropDown
                    },
                    contentDescription = null
                )
            },
            shape = MaterialTheme.shapes.small,
            modifier = Modifier
                .fillMaxWidth()
        )

        Surface(
            modifier = Modifier
                .fillMaxSize()
                .padding(8.dp)
                .clip(MaterialTheme.shapes.extraSmall)
                .clickable { expanded = true },
            color = Color.Transparent,
        ) {


        }

        if (expanded) {
            Dialog(
                onDismissRequest = { expanded = false },
            ) {
                Surface(
                    shape = RoundedCornerShape(24.dp)
                ) {
                    val listState = rememberLazyListState()

                    if (selectedIndex > -1) {
                        LaunchedEffect("ScrollToSelected") {
                            listState.scrollToItem(index = selectedIndex)
                        }
                    }

                    LazyColumn(
                        modifier = Modifier
                            .fillMaxWidth(),
                        state = listState,
                    ) {
                        itemsIndexed(items) { index, item ->
                            LargeDropdownMenuItem(
                                text = item,
                                isSelected = index == selectedIndex,
                                onClick = {
                                    onItemSelected(index)
                                    expanded = false
                                }
                            )

                            if (index < items.lastIndex) {
                                HorizontalDivider(
                                    Modifier
                                        .padding(horizontal = 16.dp),
                                )
                            }

                        }
                    }
                }
            }
        }
    }
}


@Preview
@Composable
fun LargeDropdownMenuPreview() {
    MaterialTheme {
        val items = listOf("Item 1", "Item 2", "Item 3")
        LargeDropdownMenu(
            label = "Label",
            items = items,
            selectedIndex = 0,
            onItemSelected = {},
            modifier = Modifier.background(
                color = MaterialTheme.colorScheme.background
            )
        )
    }
}
